import * as ExpenseCalculators from 'app/calcs/expenses'
import { ISavingsFixedBalances } from 'app/calcs/retirementSavings'
import { MaterialInput } from 'app/components'
import MaterialNumber from 'app/components/material/MaterialNumber';
import { IQuestionsGroupContainerProps, QuestionsGroupContainer } from 'app/containers/PipelineSection/QuestionGroupContainer'
import { getMemberContributionsChart } from 'app/containers/PipelineSection/Retirement/charts'
import { IRetirementPerson, Profile } from 'app/redux/Profile/model'
import { personPossessive, toCurrency } from 'app/utils'
import { TSavingsCalculator } from 'app/workbookDAOs/SavingsWorkbookDAO'
import { getBrandHelpers } from 'config'
import * as React from 'react'
import { Button, Col, Row } from 'react-bootstrap'
import * as ReactHighcharts from 'react-highcharts'

interface IDcAccounts extends ISavingsFixedBalances {
  chartConfig: Highcharts.Options
  personFirstNamePossessive: string
}

interface IRetirementMemberContributionsState extends IDcAccounts {
  allDcAccounts: string
  profile: Profile
}

class SavingsContributionBase extends QuestionsGroupContainer<IQuestionsGroupContainerProps, IRetirementMemberContributionsState> {
  retirementSavingsNS = 'retirement.{person}.fixedBalances.retirementSavings'
  fourxxBalanceNS = 'retirement.{person}.fixedBalances.fourxxBalance'
  iraBalanceNS = 'retirement.{person}.fixedBalances.iraBalance'
  personalSavingsNS = 'retirement.{person}.fixedBalances.personalSavings'
  healthReimbursementAccountNS = 'retirement.{person}.fixedBalances.healthReimbursementAccount'

  state: IRetirementMemberContributionsState = {
    retirementSavings: this.getValueForQuestion(this.retirementSavingsNS),
    fourxxBalance: this.getValueForQuestion(this.fourxxBalanceNS),
    healthReimbursementAccount: this.getValueForQuestion(this.healthReimbursementAccountNS),
    iraBalance: this.getValueForQuestion(this.iraBalanceNS),
    personalSavings: this.getValueForQuestion(this.personalSavingsNS),
    allDcAccounts: '0',
    chartConfig: null,
    personFirstNamePossessive: 'Member',
    profile: null
  }

  person: IRetirementPerson
  personKind: string

  setup(personKind: string) {
    this.personKind = personKind
    this.retirementSavingsNS = this.retirementSavingsNS.replace('{person}', '{' + personKind + '}')
    this.healthReimbursementAccountNS = this.healthReimbursementAccountNS.replace('{person}', '{' + personKind + '}')
    this.fourxxBalanceNS = this.fourxxBalanceNS.replace('{person}', '{' + personKind + '}')
    this.iraBalanceNS = this.iraBalanceNS.replace('{person}', '{' + personKind + '}')
    this.personalSavingsNS = this.personalSavingsNS.replace('{person}', '{' + personKind + '}')

    this.state.retirementSavings = this.getValueForQuestion(this.retirementSavingsNS)
    this.state.healthReimbursementAccount = this.getValueForQuestion(this.healthReimbursementAccountNS)
    this.state.fourxxBalance = this.getValueForQuestion(this.fourxxBalanceNS)
    this.state.iraBalance = this.getValueForQuestion(this.iraBalanceNS)
    this.state.personalSavings = this.getValueForQuestion(this.personalSavingsNS)

    this.updateDcTotal()
  }

  update<K extends keyof ISavingsFixedBalances>(key: K, e: React.ChangeEvent<HTMLInputElement>, callback: () => any) {
    const valueNum = this.toNumber(e.currentTarget.value)
    const newState = { ...this.state }
    newState[key] = valueNum

    // tslint:disable-next-line:no-debugger
    //debugger
    this.markSignificantProfileUpdate(this.state.profile)

    this.setState(newState, () => callback())
  }

  updateDC<B extends keyof ISavingsFixedBalances>(key: B, e: React.ChangeEvent<HTMLInputElement>) {
    this.update(key, e, () => this.updateDcTotal())
  }

  updateDcTotal() {
    const { retirementSavings, fourxxBalance, healthReimbursementAccount, iraBalance, personalSavings } = this.state
    let allDcAccountsNum = 0

    if (retirementSavings) {
      allDcAccountsNum += retirementSavings
    }
    if (fourxxBalance) {
      allDcAccountsNum += fourxxBalance
    }
    if (healthReimbursementAccount) {
      allDcAccountsNum += healthReimbursementAccount
    }
    if (iraBalance) {
      allDcAccountsNum += iraBalance
    }
    if (personalSavings) {
      allDcAccountsNum += personalSavings
    }

    const chartConfig = getMemberContributionsChart(null, retirementSavings, fourxxBalance, iraBalance, personalSavings)
    let allDcAccounts = '0'

    if (!isNaN(allDcAccountsNum)) {
      allDcAccounts = toCurrency(allDcAccountsNum)
    }

    this.setState({ allDcAccounts, chartConfig })
  }

  renderMemberRetirementSavings() {
    if (!getBrandHelpers().hasIncomingSavingsBalance) { return null }
    if (this.personKind !== 'member') { return null }
    const brandInfo = this.getCustomerBrandInfo()
    const brandName = brandInfo.savingsPlanTitle || (brandInfo.brandName + ' ' + brandInfo.savingsMemberTitleSuffix)
    const retirementSavingsLocked = this.getCustomerBrandHelpers().isLockedData(this.state.profile, '.fixedBalances.retirementSavings')
    return <div className="retirementSavings">
      <MaterialNumber placeholder={brandName}
        disabled={retirementSavingsLocked}
        formattedNumberMode
        name={this.retirementSavingsNS}
        value={this.state.retirementSavings}
        onChange={(e) => this.updateDC('retirementSavings', e)} />
    </div>
  }

  render() {
    return (
      <section className="retirementSection">
        <h1>{this.state.personFirstNamePossessive} Savings Benefits</h1>
        <h3 className="lead retirementHeading">How much have you saved for retirement?</h3>
        <Row>
          <Col md={6}>

            <br />
            {this.renderMemberRetirementSavings()}

            <MaterialNumber placeholder="401(k), 403(b), 457 Plan Balance"
              formattedNumberMode
              name={this.fourxxBalanceNS}
              value={this.state.fourxxBalance}
              onChange={(e) => this.updateDC('fourxxBalance', e)} />

            <MaterialNumber placeholder="IRA Balance"
              formattedNumberMode
              name={this.iraBalanceNS}
              value={this.state.iraBalance}
              onChange={(e) => this.updateDC('iraBalance', e)} />

            <MaterialNumber placeholder="Personal Savings"
              formattedNumberMode
              name={this.personalSavingsNS}
              value={this.state.personalSavings}
              onChange={(e) => this.updateDC('personalSavings', e)} />
          </Col>

          <Col md={6} className="text-center">

            <h2 className="animated fadeIn">
              <article>
                Current balance:&nbsp;
                <a>{this.state.allDcAccounts}</a>
              </article>
            </h2>
            <p><small>(<b>Tip:</b> You can hover over data points to get more info.)</small></p>
            <ReactHighcharts className="animated fadeIn"
              config={this.state.chartConfig} />
          </Col>
        </Row>
      </section>
    )
  }
}

export {
  SavingsContributionBase
}



// WEBPACK FOOTER //
// ./src/app/containers/PipelineSection/Retirement/SavingsContributionBase/index.tsx